import VueCookies from 'vue-cookies'
import idb from './idb'
import worker from './contents/worker'
import retrieveTasks from './contents/retrieveTasks'
import { toFullWidth } from '@/utils/strings'
import { isDisplayOnPhotoList, isDisplayOnDeletedList } from '@/utils/photo_conditions'
import { isDisplayOnKokubanList, isDisplayOnKokubanDeletedList } from '@/utils/kokuban_conditions'
import { useAwsStore } from "@/stores/aws"

// 黒板を使用している写真情報を取得する
const kokubansWithPhotosFor = (constructionKokubans, constructionPhotos) => {
  // 写真リストをkokuban_hashでグループ可する
  const groupedPhotos = constructionPhotos
    ? constructionPhotos.reduce((result, photo) => {
      let currentList = result[photo.kokuban_hash] || []
      currentList.push(photo)
      result[photo.kokuban_hash] = currentList
      return result
    }, {})
    : {}
  return constructionKokubans.map(kokuban => {
    const photos = groupedPhotos[kokuban.kokuban_hash]
    return {
      ...kokuban,
      photo: photos
        ? {
            photo_key: photos[0].photo_key,
            s3path: photos[0].s3path,
            extension: photos[0].extension,
            url: photos[0].thumbnail_s_url,
            width: photos[0].width,
            height: photos[0].height
          }
        : undefined
    }   
  })
}
// 最終更新時刻テーブルから最終更新時刻を取得する
const getLatestModified = async(context, cloudConstructionId, itemType) => {
  return await (await context.dispatch('idb/getDb')).kuraemon_cloud2_latest_modified_at
  .get({
    'cloud_construction_id':cloudConstructionId,
    'item_type': itemType,
  })
}

// 検索結果を取得する
const getSearchResult = async(context, cloudConstructionId, itemType, searchWord) => {
  let result = null
  // 最終更新時刻テーブルから最終更新時刻を取得する
  const latestModified = await getLatestModified(context, cloudConstructionId, itemType)

  // 最終更新時刻が設定されている場合
  if(latestModified){
    // 検索結果テーブルより検索結果を取得する
    const searchResult = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_search_result
    .get({
      'cloud_construction_id':cloudConstructionId,
      'item_type': itemType,
      'latest_modified_at': latestModified.latest_modified_at,
      'search_word': searchWord,
    })
    // 検索結果があり場合
    if(searchResult){
      result = searchResult
    }
  }
  return result
}
// 検索結果を検索結果テーブルに保存する
const saveSearchResult = async(context, cloudConstructionId, itemType, searchWord, searchResult) =>{
  // 最終更新時刻テーブルから最終更新時刻を取得する
  const latestModified = await getLatestModified(context, cloudConstructionId, itemType)

  // 最終更新時刻が設定されている場合
  if(latestModified){
    // 検索結果を検索結果テーブルに保存する
    await (await context.dispatch('idb/getDb')).kuraemon_cloud2_search_result
    .put({
    'cloud_construction_id':cloudConstructionId,
    'item_type': itemType,
    'latest_modified_at': latestModified.latest_modified_at,
    'search_word': searchWord,
    'search_result': searchResult,
    })
  }
}

/**
 * 起動対象とするアイテムタイプを返す（既に起動中は省く）
 * @param {*} context 
 * @param {string} cloudConstructionId 工事ID
 * @param {string[]} itemTypes 対象のアイテムタイプ
 * @returns 起動対象とするアイテムタイプ。ない場合はundefined
 */
const getStartItemTypes = async (context, cloudConstructionId, itemTypes) => {
  // 既存タスクの存在チェック
  const runningItemTypes = context.getters['retrieveTasks/getRunningItemTypes']({
    cloud_construction_id: cloudConstructionId,
    item_types: itemTypes,
  });
  const startItemTypes = (() => {
    if(!runningItemTypes){
      // 既存のtaskがない場合, 全種類を起動
      return itemTypes;
    }
  
    // 起動していないtaskのみを起動対象にする(既に取得中のタスクは起動しない)
    return itemTypes.filter(it => !runningItemTypes.includes(it))
  })()

  if(0 < startItemTypes.length){
    await context.dispatch('retrieveTasks/startTasks', {
      cloud_construction_id: cloudConstructionId,
      item_types: startItemTypes
    });
    return startItemTypes
  }
}

export default {
  namespaced: true,
  modules: {
    idb,
    worker,
    retrieveTasks,
  },
  state: {
    // クラウド工事ID
    cloudConstructionId: null,
    // 工事でのログインアカウント情報
    accountInfo: null,

    // 写真をロード中
    photosLoading: false,
    // クラウド工事IDの写真一覧
    constructionPhotos: null,
    // 検索結果photoKey
    searchResultPhotoKeys: null,
    // クラウド工事IDの削除された写真一覧
    deletedConstructionPhotos: null,

    // 黒板をロード中
    kokubansLoading: false,
    // クラウド工事IDの黒板一覧
    constructionKokubans: null,
    // 検索結果kokubanHash
    searchResultKokubanHashes: null,
    // クラウド工事IDの削除された黒板一覧
    deletedConstructionKokubans: null,

    // 図面をロード中
    zumensLoading: false,
    // クラウド工事IDの図面一覧
    constructionZumens: null,
    // 図面フォルダ内の空のフォルダ名のリスト
    zumenEmptyFolderNameList: null,
    // クラウド工事IDの削除された図面一覧
    deletedConstructionZumens: null,

    // 図面エンティティをロード中
    zumenEntitiesLoading: false,
    // クラウド工事IDの図面エンティティ一覧 
    constructionZumenEntities: null,

    // 図面ページをロード中
    zumenPagesLoading: false,
    // クラウド工事IDの図面ページ一覧
    constructionZumenPages: null,
  },
  getters: {
    // クラウド工事ID
    cloudConstructionId: state => state.cloudConstructionId,
    // 工事でのログインアカウント情報
    accountInfo: state => state.accountInfo,

    // 写真をロード中
    photosLoading: state => state.photosLoading,
    // 写真一覧
    constructionPhotos: state => state.constructionPhotos,
    // 検索結果photoKey
    searchResultPhotoKeys: state => state.searchResultPhotoKeys,
    // 削除された写真一覧
    deletedConstructionPhotos: state => state.deletedConstructionPhotos,

    // 写真・撮影者一覧
    photoShootingAccounts:  state => {
      if(!state.constructionPhotos) return []
      const accounts = {};
      state.constructionPhotos
        .filter(photo => photo.shooting_account_id)
        .map( photo => {
          accounts[photo.shooting_account_id] = photo.shooting_account_name 
      })
      let accountList = []
      for (const shooting_account_id of Object.keys(accounts)) {
        accountList.push({shooting_account_id: shooting_account_id, shooting_account_name: accounts[shooting_account_id]})
      }
      return accountList;
    },

    // 黒板をロード中
    kokubansLoading: state => state.kokubansLoading,
    // 黒板一覧
    constructionKokubans: state => {
      // if (state.constructionKokubans){
      //   return kokubansWithPhotosFor(state.constructionKokubans, state.constructionPhotos)
      // }
      // return null;
      return state.constructionKokubans
    },
    // 検索結果kokubanHash
    searchResultKokubanHashes: state => state.searchResultKokubanHashes,
    // 黒板を使用している写真一覧
    kokubanPhotos: (state) => (kokubanHash) => {
      if (state.constructionPhotos){
        return state.constructionPhotos.filter(photo => photo.kokuban_hash === kokubanHash)
      }
      return null;
    },
    // 削除された黒板一覧
    deletedConstructionKokubans: state => {
      if (state.deletedConstructionKokubans){
        return kokubansWithPhotosFor(state.deletedConstructionKokubans, state.deletedConstructionPhotos)
      }
      return null;
    },

    // 図面をロード中
    zumensLoading: state => state.zumensLoading,
    // 図面一覧
    constructionZumens: state => state.constructionZumens,
    // 図面フォルダ内の空のフォルダ名のリスト
    zumenEmptyFolderNameList: state => state.zumenEmptyFolderNameList,
    // 削除された写真一覧
    deletedConstructionZumens: state => state.deletedConstructionZumens,
    // 図面エンティティをロード中
    zumenEntitiesLoading: state => state.zumenEntitiesLoading,
    // 図面ページをロード中
    zumenPagesLoading: state => state.zumenPagesLoading,
    // 図面ページ一覧
    constructionZumenPages: state => (zumenId) => {
      if(!state.constructionZumenPages) return null
      if(!state.constructionZumenEntities) return null

      // 図面IDで絞り込み
      const zumenPages = state.constructionZumenPages.filter(zumen => zumen.zumen_id === zumenId)

      // 図面エンティティ情報を追加する
      // キー：zumen_id、zumen_entity_code  
      let zumenPageEntities = zumenPages.map( zumenPage => {
        const zumenEntity = state.constructionZumenEntities
          .find( zumenEntity => zumenEntity.zumen_id === zumenPage.zumen_id && 
            zumenEntity.zumen_entity_code === zumenPage.zumen_entity_code)
            
        return{
          ...zumenPage,
          zumenEntity: zumenEntity ? zumenEntity : null,
        }
      })

      // タイミングによっては、この時点で図面エンティティ情報が取得できない場合がある
      // 図面エンティティ情報が取得できない場合（zumenEntityがnull）、ページ情報から削除する
      zumenPageEntities = zumenPageEntities.filter( zumenPage => zumenPage.zumenEntity)

      // ページ番号でソート
      return zumenPageEntities.sort((a, b) => (a.page_no - b.page_no))
    },
  },
  mutations: {
    setCloudConstructionId(state, cloudConstructionId) {
      state.cloudConstructionId = cloudConstructionId
    },
    setCloudConstructionInfoMap(state, constructions) {
      const cloudConstructionInfoMap = state.cloudConstructionInfoMap
      constructions.map(construction => {
        cloudConstructionInfoMap[construction.cloud_construction_id] = construction
      })
      state.cloudConstructionInfoMap = cloudConstructionInfoMap
    },

    setAccountInfo(state, accountInfo) {
      state.accountInfo = accountInfo
    },

    setPhotosLoading(state, photosLoading) {
      state.photosLoading = photosLoading
    },
    setConstructionPhotos(state, constructionPhotos) {
      if(state.constructionPhotos) state.constructionPhotos.length = 0;
      state.constructionPhotos = constructionPhotos
    },
    setSearchResultPhotoKeys(state, searchResultPhotoKeys) {
      if(state.searchResultPhotoKeys) state.searchResultPhotoKeys.length = 0;
      state.searchResultPhotoKeys = searchResultPhotoKeys
    },
    setDeletedConstructionPhotos(state, deletedConstructionPhotos) {
      if(state.deletedConstructionPhotos) state.deletedConstructionPhotos.length = 0;
      state.deletedConstructionPhotos = deletedConstructionPhotos
    },

    setKokubansLoading(state, kokubansLoading) {
      state.kokubansLoading = kokubansLoading
    },
    setConstructionKokubans(state, constructionKokubans) {
      if(state.constructionKokubans) state.constructionKokubans.length = 0;
      state.constructionKokubans = constructionKokubans
    },
    setSearchResultKokubanHashes(state, searchResultKokubanHashes) {
      if(state.searchResultKokubanHashes) state.searchResultKokubanHashes.length = 0;
      state.searchResultKokubanHashes = searchResultKokubanHashes
    },
    setDeletedConstructionKokubans(state, deletedConstructionKokubans) {
      if(state.deletedConstructionKokubans) state.deletedConstructionKokubans.length = 0;
      state.deletedConstructionKokubans = deletedConstructionKokubans
    },

    setZumensLoading(state, zumensLoading) {
      state.zumensLoading = zumensLoading
    },
    setConstructionZumens(state, constructionZumens) {
      if(state.constructionZumens) state.constructionZumens.length = 0;
      state.constructionZumens = constructionZumens
    },
    setDeletedConstructionZumens(state, deletedConstructionZumens) {
      if(state.deletedConstructionZumens) state.deletedConstructionZumens.length = 0;
      state.deletedConstructionZumens = deletedConstructionZumens
    },
    setZumenEmptyFolderNameList(state, zumenEmptyFolderNameList) {
      state.zumenEmptyFolderNameList = zumenEmptyFolderNameList
    },

    setZumenEntitiesLoading(state, zumenEntitiesLoading) {
      state.zumenEntitiesLoading = zumenEntitiesLoading
    },
    setConstructionZumenEntities(state, constructionZumenEntities) {
      if(state.constructionZumenEntities) state.constructionZumenEntities.length = 0;
      state.constructionZumenEntities = constructionZumenEntities
    },

    setZumenPagesLoading(state, zumenPagesLoading) {
      state.zumenPagesLoading = zumenPagesLoading
    },
    setConstructionZumenPages(state, constructionZumenPages) {
      if(state.constructionZumenPages) state.constructionZumenPages.length = 0;
      state.constructionZumenPages = constructionZumenPages
    },
  },
  actions: {
    updateAccountInfo({ commit }, accountInfo){ 
      commit('setAccountInfo', accountInfo)
    },


    // コンテンツ更新ワーカーのセット
    async setContentsStorageWorker(context, worker){ 
      await context.dispatch('worker/initializeWorker', worker)
    },
    // コンテンツ保管場所のクリア
    async deleteContentsData(context){ 
      await context.dispatch('idb/deleteAllData')
    },
    // コンテンツ保管場所の初期化
    async initializeContentsStorage(context){ 
      await context.dispatch('idb/initializeDb')
    },
    // ワーカーからメッセージを受けた時の処理
    async onReceiveWorkerMessage(context, message){
      const cloudConstructionId = context.getters.cloudConstructionId

      const allLoadedItemTypes = {
        allPhotoLoaded: 'photo',
        allKokubanLoaded: 'kokuban',
        allZumenLoaded: 'zumen', 
        allZumenEntityLoaded: 'zumenEntity',
        allZumenPageLoaded: 'zumenPage',
      }
      const allLoadedItemType = allLoadedItemTypes[message.name]
      if(allLoadedItemType){
        await context.dispatch('retrieveTasks/finishTask', {
          cloud_construction_id: message.cloud_construction_id,
          item_type: allLoadedItemType,
        })
      }

      if(message.name === 'photoLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionPhotos');
        return;
      }
      if(message.name === 'allPhotoLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionPhotos');
        await context.commit("setPhotosLoading", false)
        return;
      }
      if(message.name === 'kokubanLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionKokubans');
        return;
      }
      if(message.name === 'allKokubanLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionKokubans');
        await context.commit("setKokubansLoading", false)
        return;
      }
      if(message.name === 'zumenLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionZumens');
        return;
      }
      if(message.name === 'allZumenLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionZumens');
        await context.commit("setZumensLoading", false)
        return;
      }
      if(message.name === 'zumenEntityLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionZumenEntities');
        return;
      }
      if(message.name === 'allZumenEntityLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionZumenEntities');
        await context.commit("setZumenEntitiesLoading", false)
        return;
      }
      if(message.name === 'zumenPageLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionZumenPages');
        return;
      }
      if(message.name === 'allZumenPageLoaded' && message.cloud_construction_id === cloudConstructionId){
        await context.dispatch('checkFetchSessionId', message.fetch_session_id);
        await context.dispatch('updateConstructionZumenPages');
        await context.commit("setZumenPagesLoading", false)
        return;
      }
      if(message.name === 'errorRaised') {
        await context.commit("setPhotosLoading", false)
        await context.commit("setKokubansLoading", false)
        await context.commit("setZumensLoading", false)
        await context.commit("setZumenEntitiesLoading", false)
        await context.commit("setZumenPagesLoading", false)
        await context.dispatch('app/updateAppError', message.error, { root: true });
        return;
      }
    },
    async checkFetchSessionId(context, fetchSessionId){
      if(fetchSessionId !== VueCookies.get("session_id")){
        context.dispatch("api/logoutAccount", {}, { root: true });
      }
    },
    // クラウド工事の写真データ取得をリクエスト
    async requestRetrievePhotos(context, cloudConstructionId){ 
      context.commit("setConstructionPhotos", null)
      context.commit("setDeletedConstructionPhotos", null)
      // ロード開始フラグ
      context.commit("setPhotosLoading", true)

      // 工事ID セット
      context.commit('setCloudConstructionId', cloudConstructionId);

      const msgs = {
        photo: {
          method: "getAllPhoto",
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        context, 
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドでデータを全件取得を開始
        context.dispatch('worker/backgroundTask',{ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("photo")){
        await context.dispatch('updateConstructionPhotos')
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionPhotos(context){ 
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      // 写真一覧
      const constructionPhotos = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_photo
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(ph => isDisplayOnPhotoList(ph))
        .toArray();

      // shooting_atの降順でソートする
      constructionPhotos.sort((a, b) => (b.shooting_at - a.shooting_at))

      context.commit("setConstructionPhotos", constructionPhotos)

      // 削除済みの写真一覧
      const deletedConstructionPhotos = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_photo
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(ph => isDisplayOnDeletedList(ph))
        .toArray();

      // deleted_atの降順でソートする
      deletedConstructionPhotos.sort((a, b) => (b.deleted_at - a.deleted_at))

      context.commit("setDeletedConstructionPhotos", deletedConstructionPhotos)
    },
    // クラウド工事の写真データ再取得をリクエスト
    async requestReloadPhotos(context, cloudConstructionId){ 
      if(context.getters.photosLoading) { // 写真ロード中
        return;
      }
      await context.dispatch('initializeContentsStorage');

      // 最終更新時刻をクリアする（これで再過去まで取得するようになる）
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_latest_modified_at
        .delete([cloudConstructionId, 'photo']);

      context.dispatch("requestRetrievePhotos", cloudConstructionId)
    },
    // 写真検索結果(photo_key)のクリア
    async clearSearchResultPhotoKeys(context){
      context.commit("setSearchResultPhotoKeys", null);
    },
    // 写真検索
    async searchPhotos(context, searchWord){
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      // 検索ワードを全角変換する
      const zenkakuSearchWord = toFullWidth(searchWord)

      // 検索結果テーブルより検索結果を取得する
      const result = await getSearchResult(context, cloudConstructionId, 'photo', zenkakuSearchWord)

      // 検索結果があり場合
      if(result){
        context.commit("setSearchResultPhotoKeys", result.search_result);
        return;
      }

      // 検索処理
      const zenkakuSearchWordList = zenkakuSearchWord.split("\u3000").filter(w => w);
      if (!zenkakuSearchWordList || zenkakuSearchWordList.length < 1) {
        // 検索ワードがない場合、写真検索結果をクリア
        context.commit("setSearchResultPhotoKeys", null);
        return
      }

      const whereObj = {
        'cloud_construction_id':cloudConstructionId,
      }
      const hitRecs = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_photo
        .where(whereObj)
        .filter( photo => isDisplayOnPhotoList(photo) && ( zenkakuSearchWordList.every( word => { return photo.search_words.some( sw => sw.includes(word) ) } ) ) )
        .toArray()

      const hitPhotoKeys = hitRecs.map( ph => ph.photo_key )

      // 検索結果を検索結果テーブルに保存する
      await saveSearchResult(context, cloudConstructionId, 'photo', zenkakuSearchWord, hitPhotoKeys)

      context.commit("setSearchResultPhotoKeys", hitPhotoKeys);
    },

    // クラウド工事の黒板データ取得をリクエスト
    async requestRetrieveKokubans(context, cloudConstructionId){ 
      // 写真データの初期化
      context.commit("setConstructionPhotos", null)
      context.commit("setDeletedConstructionPhotos", null)
      // 黒板データの初期化
      context.commit("setConstructionKokubans", null)
      context.commit("setDeletedConstructionKokubans", null)

      // ロード開始フラグ
      context.commit("setPhotosLoading", true)
      context.commit("setKokubansLoading", true)

      // 工事ID セット
      context.commit('setCloudConstructionId', cloudConstructionId);
      
      const msgs = {
        photo: {
          method: "getAllPhoto", 
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
        kokuban: {
          method: "getAllKokuban",
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        context, 
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドでデータを全件取得を開始
        context.dispatch('worker/backgroundTask',{ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("photo")){
        await context.dispatch('updateConstructionPhotos')
      }
      if(!startItemTypes || !startItemTypes.includes("kokuban")){
        await context.dispatch('updateConstructionKokubans')
      }
    },
    // クラウド工事の黒板データ取得をリクエスト(黒板のみ)
    async requestRetrieveKokubansOnly(context, cloudConstructionId){ 
      // 黒板データの初期化
      context.commit("setConstructionKokubans", null)
      context.commit("setDeletedConstructionKokubans", null)

      // ロード開始フラグ
      context.commit("setKokubansLoading", true)

      // 工事ID セット
      context.commit('setCloudConstructionId', cloudConstructionId);
      
      const msgs = {
        kokuban: {
          method: "getAllKokuban",
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        context, 
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドでデータを全件取得を開始
        context.dispatch('worker/backgroundTask',{ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("kokuban")){
        await context.dispatch('updateConstructionKokubans')
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionKokubans(context){ 
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      const constructionKokubans = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_kokuban
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(kb => isDisplayOnKokubanList(kb))
        .toArray();

      context.commit("setConstructionKokubans", constructionKokubans.map(kokuban => {
        // 整理情報（黒板の裏）から絞込み項目を設定する
        kokuban.search_word_organize_info_constructions = 
          kokuban.organize_info_hierarchy.map(h => (h.value || '(空欄)'));
        return kokuban
      }))

      // 削除済みの黒板一覧
      const deletedConstructionKokubans = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_kokuban
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter(kb => isDisplayOnKokubanDeletedList(kb))
        .toArray();

      // deleted_atの降順でソートする
      deletedConstructionKokubans.sort((a, b) => (b.deleted_at - a.deleted_at))

      context.commit("setDeletedConstructionKokubans", deletedConstructionKokubans)
    },
    // クラウド工事の黒板と写真データ再取得をリクエスト
    async requestReloadKokubans(context, cloudConstructionId){ 
      if(context.getters.kokubansLoading) { // 黒板ロード中
        return;
      }
      if(context.getters.photosLoading) { // 写真ロード中
        return;
      }
      await context.dispatch('initializeContentsStorage');

      // 最終更新時刻をクリアする（これで再過去まで取得するようになる）
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_latest_modified_at
        .delete([cloudConstructionId, 'kokuban']);
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_latest_modified_at
        .delete([cloudConstructionId, 'photo']);

      context.dispatch("requestRetrieveKokubans", cloudConstructionId)
    },
    // 黒板検索結果(kokuban_hash)のクリア
    async clearSearchResultKokubanHashes(context){
      context.commit("setSearchResultKokubanHashes", null);
    },
    // 黒板検索
    async searchKokubans(context, searchWord){
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      // 検索ワードを全角変換する
      const zenkakuSearchWord = toFullWidth(searchWord)

      // 検索結果テーブルより検索結果を取得する
      const result = await getSearchResult(context, cloudConstructionId, 'kokuban', zenkakuSearchWord)

      // 検索結果cacheがあり場合
      if(result){
        context.commit("setSearchResultKokubanHashes", result.search_result);
        return;
      }
      // 検索結果cacheなしの場合に、検索実施
      const zenkakuSearchWordList = zenkakuSearchWord.split("\u3000").filter(w => w);
      if (!zenkakuSearchWordList || zenkakuSearchWordList.length < 1) {
        // 検索ワードがない場合、写真検索結果をクリア
        context.commit("setSearchResultKokubanHashes", null);
        return
      }

      const whereObj = {
        'cloud_construction_id':cloudConstructionId,
      }
      const hitRecs = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_kokuban
        .where(whereObj)
        .filter( kokuban => isDisplayOnKokubanList(kokuban) && ( zenkakuSearchWordList.every( word => { return kokuban.search_words.some( sw => sw.includes(word) ) } ) ) )
        .toArray()

      const hitKokubanHashes = hitRecs.map( kk => kk.kokuban_hash )

      // 検索結果を検索結果テーブルに保存する
      await saveSearchResult(context, cloudConstructionId, 'kokuban', zenkakuSearchWord, hitKokubanHashes)

      context.commit("setSearchResultKokubanHashes", hitKokubanHashes);
    },
    // 指定工事のデータを削除する
    async deleteConstruction(context, cloudConstructionId){
      await context.dispatch('initializeContentsStorage');

      // 写真データの初期化
      context.commit("setConstructionPhotos", null)
      context.commit("setDeletedConstructionPhotos", null)

      // 黒板データの初期化
      context.commit("setConstructionKokubans", null)
      context.commit("setDeletedConstructionKokubans", null)

      // 図面データの初期化
      context.commit("setConstructionZumens", null)
      context.commit("setDeletedConstructionZumens", null)
      context.commit("setZumenEmptyFolderNameList", null)

      // 図面エンティティの初期化
      context.commit("setConstructionZumenEntities", null)

      // 図面ページの初期化
      context.commit("setConstructionZumenPages", null)

      // 工事ID
      context.commit('setCloudConstructionId', null);

      // 写真削除
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_photo
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 黒板削除
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_kokuban
        .where('cloud_construction_id').equals(cloudConstructionId).delete();
  
      // 図面削除
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 図面フォルダ内の空のフォルダ名のリスト削除
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_construction_empty_folder_name_parts_list
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 図面エンティティ削除
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen_entity
        .where('cloud_construction_id').equals(cloudConstructionId).delete();

      // 図面ページ削除
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen_page
        .where('cloud_construction_id').equals(cloudConstructionId).delete();
    },
    // バックグラウンドタスクの停止
    async stopWorkerJobs(context){ 
      await context.dispatch('initializeContentsStorage');
      //現在のジョブを全て停止する
      await (await context.dispatch('idb/getDb')).kuraemon_cloud2_job.clear();
      await context.dispatch('retrieveTasks/clearTasks')
    },

    // クラウド工事の図面データ取得をリクエスト
    async requestRetrieveZumens(context, cloudConstructionId){ 
      // 図面データの初期化
      context.commit("setConstructionZumens", null)
      context.commit("setDeletedConstructionZumens", null)
      context.commit("setZumenEmptyFolderNameList", null)
      context.commit("setConstructionZumenEntities", null)
      context.commit("setConstructionZumenPages", null)
      
      // ロード開始フラグ
      context.commit("setZumensLoading", true)
      context.commit("setZumenEntitiesLoading", true)
      context.commit("setZumenPagesLoading", true)

      // 工事ID セット後、既存の画像を表示する
      context.commit('setCloudConstructionId', cloudConstructionId);
      // context.dispatch('updateConstructionZumens');
      
      const msgs = {
        zumen: {
          method: "getAllZumen",
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
        zumenEntity: {
          method: "getAllZumenEntity",
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
        zumenPage: {
          method: "getAllZumenPage",
          cloud_construction_id: cloudConstructionId,
          accountInfo: context.state.accountInfo,
          fetch_session_id: VueCookies.get("session_id"),
        },
      }

      // 開始対象とするアイテムを取得
      const startItemTypes = await getStartItemTypes(
        context, 
        cloudConstructionId,
        Object.keys(msgs)
      )

      if(startItemTypes){
        const messages = startItemTypes.map(it => msgs[it])
        // バックグラウンドで図面データを全件取得を開始
        context.dispatch('worker/backgroundTask',{ messages })
      }

      // 既にタスク開始中のアイテムについて、一回ロードする
      if(!startItemTypes || !startItemTypes.includes("zumen")){
        await context.dispatch('updateConstructionZumens')
      }
      if(!startItemTypes || !startItemTypes.includes("zumenEntity")){
        await context.dispatch('updateConstructionZumenEntities')
      }
      if(!startItemTypes || !startItemTypes.includes("zumenPage")){
        await context.dispatch('updateConstructionZumenPages')
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionZumens(context){ 
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      // 図面一覧
      const constructionZumens = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .filter( zumen => zumen.zumen_status === 'valid' || zumen.zumen_status === 'processing' || zumen.zumen_status === 'error')
        .toArray();

      // created_atの降順でソートする
      constructionZumens.sort((a, b) => (b.created_at - a.created_at))

      context.commit("setConstructionZumens", constructionZumens)

      // 削除済みの図面一覧
      const deletedConstructionZumens = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen
        .where({
          'cloud_construction_id':cloudConstructionId,
          'zumen_status': 'deleted',
        })
        .toArray();

      // deleted_atの降順でソートする
      deletedConstructionZumens.sort((a, b) => (b.deleted_at - a.deleted_at))

      context.commit("setDeletedConstructionZumens", deletedConstructionZumens)

      // 図面フォルダ内の空のフォルダ名のリスト
      const zumenEmptyFolderNameList = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_construction_empty_folder_name_parts_list
        .get({'cloud_construction_id':cloudConstructionId})
      
      if (zumenEmptyFolderNameList){
        context.commit("setZumenEmptyFolderNameList", zumenEmptyFolderNameList.folder_name_list)
      }
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionZumenEntities(context){ 
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      // 図面エンティティ一覧
      const constructionEntities = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen_entity
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .toArray();

      context.commit("setConstructionZumenEntities", constructionEntities)
    },
    // バックグラウンドで取得したデータを取得する
    async updateConstructionZumenPages(context){ 
      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.state.cloudConstructionId;

      // 図面ページ一覧
      const constructionPages = await (await context.dispatch('idb/getDb')).kuraemon_cloud2_zumen_page
        .where({
          'cloud_construction_id':cloudConstructionId,
        })
        .toArray();

      context.commit("setConstructionZumenPages", constructionPages)
    },

    // 図面エンティティのサムネイルのURLを更新する
    async updateZumenUrl(context, zumenEntity){
      const awsStore = useAwsStore()

      await context.dispatch('initializeContentsStorage');
      const cloudConstructionId = context.getters.cloudConstructionId

      // URLの生成 
      const args = {
        cloud_construction_id: cloudConstructionId,
        zumenEntity: zumenEntity,
      };
      const thumbnail = await awsStore.buildZumenUrl(args)
      return thumbnail
    },
  }
}
